import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Users, IndianRupee, Ticket, RotateCcw, Upload, Trash2, Download, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loading } from "@/components/shared";
import api from "@/lib/api";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-navy/5 text-navy"><Icon className="h-[18px] w-[18px]" strokeWidth={1.75} /></div>
      <p className="mt-3 font-heading text-2xl font-bold text-navy">{value}</p>
      <p className="text-sm text-slate-500">{label}</p>
      {sub && <p className="mt-0.5 text-xs font-semibold text-emerald">{sub}</p>}
    </div>
  );
}

function SettingsCard({ onSaved }) {
  const { data, isLoading } = useQuery({ queryKey: ["sm-settings"], queryFn: async () => (await api.get("/sales-mastery/admin/settings")).data });
  const [form, setForm] = useState(null);
  React.useEffect(() => { if (data && !form) setForm(data); }, [data]);
  if (isLoading || !form) return <Loading />;
  const upd = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    try {
      await api.put("/sales-mastery/admin/settings", {
        launch_price: Number(form.launch_price), standard_price: Number(form.standard_price),
        launch_cap: Number(form.launch_cap), launch_end_date: form.launch_end_date || null,
        refund_days: Number(form.refund_days),
      });
      toast.success("Settings saved"); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="sm-settings-card">
      <h3 className="font-heading font-semibold text-navy">Course settings</h3>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div><Label className="text-xs">Launch price (₹)</Label><Input type="number" className="mt-1" value={form.launch_price} onChange={(e) => upd("launch_price", e.target.value)} data-testid="sm-launch-price" /></div>
        <div><Label className="text-xs">Standard price (₹)</Label><Input type="number" className="mt-1" value={form.standard_price} onChange={(e) => upd("standard_price", e.target.value)} data-testid="sm-standard-price" /></div>
        <div><Label className="text-xs">Launch cap (buyers)</Label><Input type="number" className="mt-1" value={form.launch_cap} onChange={(e) => upd("launch_cap", e.target.value)} data-testid="sm-launch-cap" /></div>
        <div><Label className="text-xs">Launch end date</Label><Input type="date" className="mt-1" value={(form.launch_end_date || "").slice(0, 10)} onChange={(e) => upd("launch_end_date", e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="sm-launch-end" /></div>
        <div><Label className="text-xs">Refund window (days)</Label><Input type="number" className="mt-1" value={form.refund_days} onChange={(e) => upd("refund_days", e.target.value)} data-testid="sm-refund-days" /></div>
      </div>
      <Button onClick={save} className="mt-5 rounded-md bg-emerald text-white hover:bg-emerald-700" data-testid="sm-save-settings"><Save className="mr-2 h-4 w-4" /> Save settings</Button>
    </div>
  );
}

function ModulesCard() {
  const qc = useQueryClient();
  const { data: course, isLoading } = useQuery({ queryKey: ["sm-course"], queryFn: async () => (await api.get("/courses/ultimate-sales-mastery")).data });
  const [edits, setEdits] = useState({});
  if (isLoading) return <Loading />;
  const save = async (m) => {
    const e = edits[m.id] || {};
    try {
      await api.put("/sales-mastery/admin/modules", {
        module_id: m.id,
        coming_soon: e.coming_soon !== undefined ? e.coming_soon : m.coming_soon,
        expected_date: e.expected_date !== undefined ? e.expected_date : (m.expected_date || ""),
      });
      toast.success("Module updated"); qc.invalidateQueries({ queryKey: ["sm-course"] });
    } catch (err) { toast.error("Update failed"); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="sm-modules-card">
      <h3 className="font-heading font-semibold text-navy">Modules & release dates</h3>
      <div className="mt-4 space-y-3">
        {(course?.modules || []).map((m, i) => {
          const e = edits[m.id] || {};
          const cs = e.coming_soon !== undefined ? e.coming_soon : m.coming_soon;
          const ed = e.expected_date !== undefined ? e.expected_date : (m.expected_date || "");
          return (
            <div key={m.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-100 p-3" data-testid={`sm-module-${i + 1}`}>
              <span className="flex-1 text-sm font-semibold text-navy">{i + 1}. {m.title}</span>
              <label className="flex items-center gap-1.5 text-xs text-slate-600">
                <input type="checkbox" checked={cs} onChange={(ev) => setEdits({ ...edits, [m.id]: { ...e, coming_soon: ev.target.checked } })} data-testid={`sm-module-coming-${i + 1}`} /> Coming soon
              </label>
              <Input placeholder="Expected date e.g. 15 Jul" className="h-8 w-40 text-xs" value={ed} onChange={(ev) => setEdits({ ...edits, [m.id]: { ...e, expected_date: ev.target.value } })} data-testid={`sm-module-date-${i + 1}`} />
              <Button size="sm" variant="outline" onClick={() => save(m)} data-testid={`sm-module-save-${i + 1}`}>Save</Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TemplateRow({ t, onChange }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [avail, setAvail] = useState(t.available_date || "");
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/sales-mastery/admin/templates/${t.id}`, {
        name: t.name, module: t.module, module_title: t.module_title, file_type: t.file_type,
        description: t.description || "", file_url: up.url, file_name: up.filename,
        status: "available", available_date: avail,
      });
      toast.success(`Uploaded ${t.name}`); onChange();
    } catch (e) { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  const saveMeta = async () => {
    try {
      await api.put(`/sales-mastery/admin/templates/${t.id}`, {
        name: t.name, module: t.module, module_title: t.module_title, file_type: t.file_type,
        description: t.description || "", file_url: t.file_url || "", file_name: t.file_name || "",
        status: t.file_url ? "available" : "coming_soon", available_date: avail,
      });
      toast.success("Saved"); onChange();
    } catch (e) { toast.error("Save failed"); }
  };
  const del = async () => { try { await api.delete(`/sales-mastery/admin/templates/${t.id}`); toast.success("Deleted"); onChange(); } catch { toast.error("Delete failed"); } };
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-100 p-3" data-testid={`sm-template-${t.id}`}>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-navy">M{t.module} · {t.name} <span className="text-[11px] font-medium text-slate-400">{t.file_type}</span></p>
        <p className="text-xs text-slate-500">{t.file_url ? <a href={t.file_url} target="_blank" rel="noreferrer" className="text-emerald hover:underline">File attached</a> : <span className="text-amber-600">No file yet</span>}</p>
      </div>
      <Input placeholder="Available date" className="h-8 w-36 text-xs" value={avail} onChange={(e) => setAvail(e.target.value)} data-testid={`sm-template-date-${t.id}`} />
      <input ref={fileRef} type="file" hidden onChange={(e) => upload(e.target.files[0])} data-testid={`sm-template-file-${t.id}`} />
      <Button size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`sm-template-upload-${t.id}`}><Upload className="mr-1.5 h-3.5 w-3.5" /> {busy ? "…" : "Upload"}</Button>
      <Button size="sm" variant="outline" onClick={saveMeta}>Save</Button>
      <Button size="sm" variant="ghost" onClick={del} className="text-red-500 hover:text-red-600" data-testid={`sm-template-del-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );
}

function TemplatesCard() {
  const { data, isLoading, refetch } = useQuery({ queryKey: ["sm-templates-admin"], queryFn: async () => (await api.get("/sales-mastery/admin/templates")).data });
  if (isLoading) return <Loading />;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="sm-templates-card">
      <h3 className="font-heading font-semibold text-navy">Templates</h3>
      <p className="mt-1 text-sm text-slate-500">Upload the working file for each template. Buyers see "Available [date]" until a file is attached.</p>
      <div className="mt-4 space-y-2">
        {(data || []).map((t) => <TemplateRow key={t.id} t={t} onChange={refetch} />)}
      </div>
    </div>
  );
}

export default function SalesMasteryAdmin() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["sm-dashboard"], queryFn: async () => (await api.get("/sales-mastery/admin/dashboard")).data });
  const refund = async (id) => {
    if (!window.confirm("Refund this purchase and revoke access?")) return;
    try { await api.post(`/sales-mastery/admin/purchases/${id}/refund`); toast.success("Refunded"); qc.invalidateQueries({ queryKey: ["sm-dashboard"] }); }
    catch (e) { toast.error("Refund failed"); }
  };
  const downloadCsv = async () => {
    try {
      const res = await api.get("/sales-mastery/admin/purchases.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "sales-mastery-buyers.csv"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  if (isLoading) return <Loading />;
  return (
    <div className="space-y-6" data-testid="sales-mastery-admin">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Users} label="Buyers" value={data.buyers} />
        <Stat icon={IndianRupee} label="Revenue" value={inr(data.revenue)} />
        <Stat icon={Ticket} label="Launch seats left" value={`${data.seats_remaining} / ${data.launch_cap}`} sub={data.launch_active ? `Launch price live · ${inr(data.current_price)}` : `Standard · ${inr(data.current_price)}`} />
        <Stat icon={RotateCcw} label="Refunds" value={data.refunds} />
      </div>

      <SettingsCard onSaved={() => qc.invalidateQueries({ queryKey: ["sm-dashboard"] })} />
      <ModulesCard />
      <TemplatesCard />

      <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="sm-purchases-card">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-semibold text-navy">Buyers</h3>
          <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="sm-csv-btn"><Download className="mr-1.5 h-3.5 w-3.5" /> CSV</Button>
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-400">
              <th className="py-2">Name</th><th>Email</th><th>Amount</th><th>Type</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {(data.purchases || []).length === 0 && <tr><td colSpan={6} className="py-6 text-center text-slate-400">No buyers yet.</td></tr>}
              {(data.purchases || []).map((p) => (
                <tr key={p.id} className="border-b border-slate-100" data-testid={`sm-purchase-${p.id}`}>
                  <td className="py-2 font-medium text-navy">{p.name || "—"}</td>
                  <td className="text-slate-600">{p.email}</td>
                  <td>{inr(p.amount)}</td>
                  <td className="capitalize text-slate-600">{p.price_type}</td>
                  <td><span className={`rounded px-2 py-0.5 text-[11px] font-bold ${p.status === "paid" ? "bg-emerald/10 text-emerald" : "bg-slate-100 text-slate-500"}`}>{p.status}</span></td>
                  <td>{p.status === "paid" && <Button size="sm" variant="ghost" onClick={() => refund(p.id)} className="text-red-500 hover:text-red-600" data-testid={`sm-refund-${p.id}`}>Refund</Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
